import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

import { Modal } from '@/components/core/Modal'
import { useUnsavedChanges } from '@/context/UnsavedChangesContext'

type ConfirmOptions = {
  title?: string
  description?: string | ReactNode
  confirmText?: string
  cancelText?: string
}

type Pending = {
  action: () => void
  options?: ConfirmOptions
} | null

interface LeaveModalContextValue {
  hasUnsavedChanges: boolean
  setHasUnsavedChanges: (value: boolean) => void
  confirmNavigation: (action: () => void, options?: ConfirmOptions) => void
}

const LeaveModalContext = createContext<LeaveModalContextValue | undefined>(undefined)

/**
 * Global flag to bypass beforeunload check during 401 redirect
 * This is checked synchronously in the beforeunload handler
 */
export let shouldBypassBeforeUnload = false

/**
 * Set the bypass flag (called from 401 handler)
 */
export function setBypassBeforeUnload(value: boolean): void {
  shouldBypassBeforeUnload = value
}

export function LeaveModalProvider({ children }: { children: ReactNode }) {
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges()
  const [pending, setPending] = useState<Pending>(null)
  const [isOpen, setIsOpen] = useState(false)

  const confirmNavigation = (action: () => void, options?: ConfirmOptions) => {
    if (!hasUnsavedChanges) {
      action()
      return
    }
    setPending({ action, options })
    setIsOpen(true)
  }

  const handleCancel = () => {
    setIsOpen(false)
    setPending(null)
  }

  const handleConfirm = () => {
    const toRun = pending?.action
    setIsOpen(false)
    setPending(null)
    if (toRun) toRun()
  }

  // Global beforeunload handler based on unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Check bypass flag first (synchronous check for 401 redirects)
      if (shouldBypassBeforeUnload) {
        return // Allow navigation without dialog
      }

      e.preventDefault()
      e.returnValue = ''
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  const value: LeaveModalContextValue = {
    hasUnsavedChanges,
    setHasUnsavedChanges,
    confirmNavigation,
  }

  const title = pending?.options?.title ?? 'Leave this page?'
  const description =
    pending?.options?.description ?? 'You have unsaved changes. If you leave now, your changes will be lost.'
  const confirmText = pending?.options?.confirmText ?? 'Leave'
  const cancelText = pending?.options?.cancelText ?? 'Cancel'

  return (
    <LeaveModalContext.Provider value={value}>
      {children}
      <Modal
        isOpen={isOpen}
        onClose={handleCancel}
        title={title}
        description={description}
        cancelText={cancelText}
        confirmText={confirmText}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </LeaveModalContext.Provider>
  )
}

export function useLeaveModal(): LeaveModalContextValue {
  const ctx = useContext(LeaveModalContext)
  if (!ctx) throw new Error('useLeaveModal must be used within LeaveModalProvider')
  return ctx
}
